/**
 * Portal authentication route loader.
 *
 * Validates portal tokens before allowing access to protected routes.
 * Uses shared utilities from @/core/auth for consistent token handling.
 */

import { redirect, LoaderFunctionArgs } from 'react-router-dom';
import { validatePortalToken } from '@/core/auth/tokenUtils';
import { getPortalAuth, clearPortalAuth } from '@/core/auth/storage';
import { attemptTokenRefresh } from '@/core/auth/refreshToken';
import type { TokenValidationResult } from '@/core/auth/types';

// Re-export for backwards compatibility
export { validatePortalToken as validateToken } from '@/core/auth/tokenUtils';

/**
 * Legacy function for backwards compatibility.
 */
export const isTokenValid = (token: string, expectedClientId: string): boolean => {
  const result = validatePortalToken(token, expectedClientId);
  return result.valid;
};

/**
 * Build a `/portal/login?redirect=<originating>` URL preserving the path
 * the user was actually trying to reach. The loader runs before
 * ``ApprovalView`` mounts, so without this the deep-link suffix
 * (e.g. ``/ideas``) is lost when the redirect cascades.
 */
function loginUrlPreserving(request: Request): string {
  const url = new URL(request.url);
  const originPath = `${url.pathname}${url.search}`;
  if (!originPath || originPath === '/portal/login') return '/portal/login';
  return `/portal/login?redirect=${encodeURIComponent(originPath)}`;
}

/**
 * Append the originating path to error screens so they can offer a
 * "request a new link" button that lands the user back on the resource
 * they were trying to reach.
 */
function appendRedirect(targetPath: string, request: Request): string {
  const url = new URL(request.url);
  const originPath = `${url.pathname}${url.search}`;
  if (!originPath) return targetPath;
  return `${targetPath}?redirect=${encodeURIComponent(originPath)}`;
}

/**
 * Route loader that validates portal authentication.
 *
 * Validates token before allowing navigation to protected routes.
 * When the access token is expired, attempts a transparent refresh
 * using the stored refresh token before giving up.
 */
export const portalAuthLoader = async ({ params, request }: LoaderFunctionArgs) => {
  const clientId = params.clientId;

  if (!clientId) {
    console.warn('[portalAuthLoader] No clientId in route params');
    throw redirect(loginUrlPreserving(request));
  }

  const auth = getPortalAuth(clientId);

  if (!auth) {
    console.log('[portalAuthLoader] No stored credentials, bouncing to login');
    throw redirect(loginUrlPreserving(request));
  }

  const { token, role } = auth;
  const validation = validatePortalToken(token, clientId);

  if (!validation.valid) {
    console.log('[portalAuthLoader] Token invalid, reason:', validation.reason);

    // For expired tokens, attempt a refresh before giving up
    if (validation.reason === 'expired') {
      console.log('[portalAuthLoader] Attempting token refresh');
      const refreshResult = await attemptTokenRefresh(clientId);
      if (refreshResult.success && refreshResult.token) {
        console.log('[portalAuthLoader] Token refreshed successfully');
        return { token: refreshResult.token, role, clientId };
      }
      console.log('[portalAuthLoader] Refresh failed, bouncing to login');
    }

    clearPortalAuth(clientId);

    switch (validation.reason) {
      case 'expired':
        throw redirect(appendRedirect(`/client/${clientId}/expired`, request));
      case 'wrong_client':
        throw redirect(appendRedirect(`/client/${clientId}/unauthorized`, request));
      default:
        throw redirect(loginUrlPreserving(request));
    }
  }

  console.log('[portalAuthLoader] Token valid, allowing navigation');
  return { token, role, clientId };
};
